import { createAuth0Gateway, type Auth0Gateway } from '../auth0/gateway';
import {
  createOrganizationCoordinator,
  type OrganizationCoordinator,
} from '../organization/coordinator';
import {
  AUTH_CALLBACK_MAX_ATTEMPTS,
  AUTH_CALLBACK_RETRY_DELAY_MS,
  createSessionSyncKey,
  isMfaRequiredAuth0Error,
  isMissingIdTokenError,
  isMissingOrganizationScopedAccessTokenError,
  isSessionSyncTimeoutError,
  isUnauthenticatedAuth0Error,
  sleep,
  withTimeout,
} from './sync-utils';
import { createAuthSessionFlowTrace, logAuthSessionFlow } from './tracing';
import type { AuthSessionSnapshot } from './auth-session-state';
import type {
  Auth0ClientLike,
  AuthSessionFlowTrace,
  AuthSessionStatus,
  EnsureSessionOptions,
  EnsureSessionResult,
  UserContextStoreLike,
} from './types';

type FailedSessionResult = Extract<EnsureSessionResult, { status: 'failed' }>;

const TRUSTED_AUTH_REFRESH_DEDUPE_WINDOW_MS = 1_000;

class StaleTrustedRefreshCommitError extends Error {
  constructor() {
    super('Stale trusted auth refresh result.');
    this.name = 'StaleTrustedRefreshCommitError';
  }
}

type SessionSyncContext = {
  reason: NonNullable<EnsureSessionOptions['reason']>;
  auth0Client: Auth0ClientLike | null;
  auth0ClientIdentity: string;
  hasCommittedSession: boolean;
  hasLocalSession: boolean;
  previousSessionSnapshot: AuthSessionSnapshot;
  shouldPreserveExistingSession: boolean;
  hasAccessTokenLoader: boolean;
  flowTrace: AuthSessionFlowTrace;
};

type SessionRunOptions = {
  dedupeSuffix?: string;
  flowTrace?: AuthSessionFlowTrace;
};

type AuthSessionOrchestratorDependencies = {
  sessionSyncPromises: Map<string, Promise<EnsureSessionResult>>;
  userContextStore: UserContextStoreLike;
  setStatus: (nextStatus: AuthSessionStatus, error?: unknown) => void;
  setSession: (session: { accessToken: string; nextIdToken: string }) => void;
  clearAuth: () => void;
  getSessionResultFromState: (
    options?: EnsureSessionOptions,
  ) => Extract<EnsureSessionResult, { status: 'authenticated' }> | null;
  captureSessionSnapshot: () => AuthSessionSnapshot;
  restoreSessionSnapshot: (snapshot: AuthSessionSnapshot) => void;
  getJwtToken: () => string | null;
  getIdToken: () => string | null;
  getOrganizationId: () => string | null;
  getRequiredOrganizationId: () => string | null;
  requiresBasicInfoCollection: () => boolean;
  isTokenExpired: () => boolean;
  onMfaRequired?: () => Promise<void>;
  auth0Gateway?: Auth0Gateway;
  organizationCoordinator?: OrganizationCoordinator;
};

export const createAuthSessionOrchestrator = ({
  sessionSyncPromises,
  userContextStore,
  setStatus,
  setSession,
  clearAuth,
  getSessionResultFromState,
  captureSessionSnapshot,
  restoreSessionSnapshot,
  getJwtToken,
  getIdToken,
  getOrganizationId,
  getRequiredOrganizationId,
  requiresBasicInfoCollection,
  isTokenExpired,
  onMfaRequired,
  auth0Gateway: auth0GatewayOverride,
  organizationCoordinator: organizationCoordinatorOverride,
}: AuthSessionOrchestratorDependencies) => {
  const auth0Gateway = auth0GatewayOverride ?? createAuth0Gateway();
  const organizationCoordinator =
    organizationCoordinatorOverride ?? createOrganizationCoordinator();

  const auth0ClientIdentities = new WeakMap<object, string>();
  const trustedRefreshPromises = new Map<string, Promise<string | null>>();
  const recentTrustedRefreshes = new Map<
    string,
    {
      accessToken: string;
      completedAt: number;
      idToken: string | null;
      organizationId: string | null;
    }
  >();
  let auth0ClientIdentityCounter = 0;

  const getAuth0ClientIdentity = (client: Auth0ClientLike | null): string => {
    if (!client) {
      return 'auth0-client:none';
    }

    const key = client as object;
    const existingIdentity = auth0ClientIdentities.get(key);

    if (existingIdentity) {
      return existingIdentity;
    }

    const nextIdentity = `auth0-client:${++auth0ClientIdentityCounter}`;
    auth0ClientIdentities.set(key, nextIdentity);

    return nextIdentity;
  };

  const createSessionSyncPromiseKey = (
    options: EnsureSessionOptions | undefined,
    context: Pick<SessionSyncContext, 'auth0ClientIdentity'>,
    runOptions?: SessionRunOptions,
  ) => {
    return `${createSessionSyncKey(options)}::${context.auth0ClientIdentity}::${
      runOptions?.dedupeSuffix ?? ''
    }`;
  };

  const createSessionSyncContext = (
    options?: EnsureSessionOptions,
    runOptions?: SessionRunOptions,
  ): SessionSyncContext => {
    const reason = options?.reason ?? 'request';
    const auth0Client = auth0Gateway.resolveAuth0Client(options?.auth0Client);
    const isExplicitAuth0Client = options?.auth0Client !== undefined;
    const hasCommittedSession = getSessionResultFromState(options) !== null;
    const hasLocalSession = !!getJwtToken() && !!getIdToken();
    const flowTrace =
      runOptions?.flowTrace ??
      createAuthSessionFlowTrace({
        reason,
        targetOrganizationId: options?.targetOrganizationId ?? null,
        fallbackMode: options?.fallbackMode ?? 'clear-auth',
      });

    return {
      reason,
      auth0Client,
      auth0ClientIdentity: isExplicitAuth0Client
        ? getAuth0ClientIdentity(auth0Client)
        : 'auth0-client:default',
      hasCommittedSession,
      hasLocalSession,
      previousSessionSnapshot: captureSessionSnapshot(),
      shouldPreserveExistingSession:
        options?.fallbackMode === 'preserve-existing-session' &&
        (hasCommittedSession || hasLocalSession),
      hasAccessTokenLoader: !!auth0Client?.getAccessTokenSilently,
      flowTrace,
    };
  };

  const transitionToStatus = (
    trace: AuthSessionFlowTrace,
    nextStatus: AuthSessionStatus,
    error: unknown = null,
  ) => {
    setStatus(nextStatus, error);
    logAuthSessionFlow(nextStatus === 'error' ? 'warn' : 'debug', 'Flow stage transition', trace, {
      stage: nextStatus,
      ...(error !== null ? { error } : {}),
    });
  };

  const getAuthenticatedStatus = () => {
    if (requiresBasicInfoCollection()) {
      return 'awaiting-basic-info';
    }

    if (userContextStore.requiresOrganizationCreation) {
      return 'awaiting-organization-creation';
    }

    return userContextStore.requiresOrganizationSelection
      ? 'awaiting-organization-selection'
      : 'ready';
  };

  const clearOrPreserveSessionAfterFailure = (
    context: SessionSyncContext,
    failureReason: FailedSessionResult['failureReason'],
    error?: unknown,
  ): EnsureSessionResult => {
    if (context.shouldPreserveExistingSession) {
      restoreSessionSnapshot(context.previousSessionSnapshot);
      logAuthSessionFlow(
        'warn',
        'Preserving existing session after background sync failure',
        context.flowTrace,
        {
          stage: 'ready',
          failureReason,
          ...(error ? { error } : {}),
        },
      );
      transitionToStatus(context.flowTrace, getAuthenticatedStatus(), error ?? null);

      return {
        status: 'failed',
        reason: context.reason,
        failureReason,
        ...(error ? { error } : {}),
      };
    }

    logAuthSessionFlow('warn', 'Clearing auth after session sync failure', context.flowTrace, {
      stage: 'anonymous',
      failureReason,
      ...(error ? { error } : {}),
    });
    clearAuth();

    return {
      status: 'unauthenticated',
      reason: context.reason,
    };
  };

  const logSessionSyncStart = (context: SessionSyncContext) => {
    if (context.hasCommittedSession) {
      transitionToStatus(context.flowTrace, 'refreshing');
    }

    transitionToStatus(context.flowTrace, 'waiting-for-auth0');
    logAuthSessionFlow('debug', 'Starting session sync', context.flowTrace, {
      stage: 'waiting-for-auth0',
      hasCommittedSession: context.hasCommittedSession,
      hasLocalSession: context.hasLocalSession,
    });
  };

  const getPostSettleSessionSyncResult = (
    context: SessionSyncContext,
    options?: EnsureSessionOptions,
  ): EnsureSessionResult | null => {
    if (context.auth0Client?.isAuthenticated && !context.auth0Client.isAuthenticated.value) {
      return clearOrPreserveSessionAfterFailure(context, 'auth0-error');
    }

    if (
      context.auth0Client?.error?.value &&
      !options?.ignoreAuth0Error?.(context.auth0Client.error.value) &&
      !(context.reason === 'manual-refresh' && context.shouldPreserveExistingSession)
    ) {
      throw context.auth0Client.error.value;
    }

    return null;
  };

  const finalizeSessionSync = async (
    context: SessionSyncContext,
    options?: EnsureSessionOptions,
  ): Promise<EnsureSessionResult> => {
    if (!context.auth0Client) {
      throw new Error('Missing Auth0 client');
    }

    const auth0Client = context.auth0Client;
    const isTrustedRefresh =
      options?.reason === 'manual-refresh' &&
      options.refresh === true &&
      options.freshAuth0Token === true;
    let expectedTrustedRefreshCurrentToken = context.previousSessionSnapshot.jwtToken;
    let expectedTrustedRefreshCurrentIdToken = context.previousSessionSnapshot.idToken;

    const createStaleTrustedRefreshResult = (): FailedSessionResult => {
      logAuthSessionFlow('warn', 'Ignoring stale trusted auth refresh result', context.flowTrace, {
        failureReason: 'auth0-error',
      });

      return {
        status: 'failed',
        reason: context.reason,
        failureReason: 'auth0-error',
      };
    };

    const commitSession = (session: { accessToken: string; nextIdToken: string }): boolean => {
      if (
        isTrustedRefresh &&
        (getJwtToken() !== expectedTrustedRefreshCurrentToken ||
          getIdToken() !== expectedTrustedRefreshCurrentIdToken)
      ) {
        return false;
      }

      setSession(session);
      expectedTrustedRefreshCurrentToken = session.accessToken;
      expectedTrustedRefreshCurrentIdToken = session.nextIdToken;

      return true;
    };

    transitionToStatus(context.flowTrace, 'fetching-base-session');

    const session = await auth0Gateway.getSessionFromAuth0({
      auth0Client,
      freshAuth0Token: options?.freshAuth0Token,
      // A targeted request must not temporarily clear the organization and its workflows.
      ...(options?.targetOrganizationId ? { organization: options.targetOrganizationId } : {}),
      timeoutMs: options?.timeoutMs,
    });

    if (!commitSession({ accessToken: session.accessToken, nextIdToken: session.idToken })) {
      return createStaleTrustedRefreshResult();
    }

    const scopedSession = await organizationCoordinator
      .coordinateOrganizationSession({
        auth0Client,
        currentOrganizationId: getOrganizationId(),
        options,
        userContextStore,
        trace: context.flowTrace,
        setStatus: (nextStatus, error) => transitionToStatus(context.flowTrace, nextStatus, error),
        setSession: (nextSession) => {
          if (!commitSession(nextSession)) {
            throw new StaleTrustedRefreshCommitError();
          }
        },
      })
      .catch((error: unknown) => {
        if (error instanceof StaleTrustedRefreshCommitError) {
          return createStaleTrustedRefreshResult();
        }

        throw error;
      });

    if (scopedSession.status === 'failed') {
      return scopedSession;
    }

    if (scopedSession.status === 'redirecting') {
      logAuthSessionFlow(
        'debug',
        'Session sync requires redirect re-authorization',
        context.flowTrace,
        {
          stage: 'redirecting-for-organization-scope',
          organizationId: scopedSession.targetOrganizationId,
          outcome: 'redirecting',
        },
      );

      return {
        status: 'redirecting',
        reason: context.reason,
        targetOrganizationId: scopedSession.targetOrganizationId,
      };
    }

    const finalSession = scopedSession.session ?? session;
    const nextStatus = getAuthenticatedStatus();

    transitionToStatus(context.flowTrace, nextStatus);
    logAuthSessionFlow('debug', 'Session sync authenticated successfully', context.flowTrace, {
      stage: nextStatus,
      outcome: 'authenticated',
      organizationId: getOrganizationId(),
    });

    return {
      status: 'authenticated',
      accessToken: finalSession.accessToken,
      idToken: finalSession.idToken,
      reason: context.reason,
    };
  };

  const finalizeFailedSession = (
    context: SessionSyncContext,
    error: unknown,
    failureReason: FailedSessionResult['failureReason'],
    includeError: boolean,
  ): FailedSessionResult => {
    if (context.shouldPreserveExistingSession) {
      restoreSessionSnapshot(context.previousSessionSnapshot);
    }

    transitionToStatus(
      context.flowTrace,
      context.hasCommittedSession ? getAuthenticatedStatus() : 'error',
      error,
    );

    return {
      status: 'failed',
      reason: context.reason,
      failureReason,
      ...(includeError ? { error } : {}),
    };
  };

  const handleSessionSyncFailure = async (
    context: SessionSyncContext,
    options: EnsureSessionOptions | undefined,
    error: unknown,
  ): Promise<EnsureSessionResult> => {
    if (isMissingIdTokenError(error)) {
      logAuthSessionFlow(
        'warn',
        'Session sync failed because ID token is missing',
        context.flowTrace,
        {
          failureReason: 'missing-id-token',
          error,
        },
      );

      return finalizeFailedSession(context, error, 'missing-id-token', false);
    }

    if (isMissingOrganizationScopedAccessTokenError(error)) {
      logAuthSessionFlow(
        'warn',
        'Session sync failed because access token is missing organization scope',
        context.flowTrace,
        {
          failureReason: 'auth0-error',
          error,
        },
      );

      return finalizeFailedSession(context, error, 'auth0-error', true);
    }

    if (isMfaRequiredAuth0Error(error)) {
      logAuthSessionFlow('warn', 'Session sync requires MFA reauthentication', context.flowTrace, {
        failureReason: 'auth0-error',
        error,
      });

      if (options?.freshAuth0Token || options?.suppressMfaRedirect) {
        return clearOrPreserveSessionAfterFailure(context, 'auth0-error', error);
      }

      if (onMfaRequired) {
        try {
          await onMfaRequired();
        } catch (redirectError) {
          logAuthSessionFlow(
            'warn',
            'Failed to trigger MFA reauthentication redirect',
            context.flowTrace,
            {
              failureReason: 'auth0-error',
              error: redirectError,
            },
          );
          return finalizeFailedSession(context, redirectError, 'auth0-error', true);
        }

        return {
          status: 'redirecting',
          reason: context.reason,
        };
      }

      return clearOrPreserveSessionAfterFailure(context, 'auth0-error', error);
    }

    if (isUnauthenticatedAuth0Error(error)) {
      return clearOrPreserveSessionAfterFailure(context, 'auth0-error', error);
    }

    logAuthSessionFlow('warn', 'Session sync failed with Auth0 error', context.flowTrace, {
      failureReason: 'auth0-error',
      error,
    });

    if (context.shouldPreserveExistingSession) {
      return clearOrPreserveSessionAfterFailure(context, 'auth0-error', error);
    }

    transitionToStatus(
      context.flowTrace,
      context.hasCommittedSession ? getAuthenticatedStatus() : 'error',
      error,
    );

    return {
      status: 'failed',
      reason: context.reason,
      failureReason: 'auth0-error',
      error,
    };
  };

  const getCachedEnsureSessionResult = (
    options?: EnsureSessionOptions,
    runOptions?: SessionRunOptions,
  ): EnsureSessionResult | null => {
    const shouldRefreshSession = options?.refresh ?? false;
    const existingSession = getSessionResultFromState(options);

    if (shouldRefreshSession || !existingSession) {
      return null;
    }

    const cachedFlowTrace =
      runOptions?.flowTrace ??
      createAuthSessionFlowTrace({
        reason: options?.reason ?? 'request',
        targetOrganizationId: options?.targetOrganizationId ?? null,
        fallbackMode: options?.fallbackMode ?? 'clear-auth',
      });

    const nextStatus = getAuthenticatedStatus();
    transitionToStatus(cachedFlowTrace, nextStatus);
    logAuthSessionFlow('debug', 'Reusing cached authenticated session', cachedFlowTrace, {
      stage: nextStatus,
      outcome: 'cached-session',
    });

    return existingSession;
  };

  const runSessionSync = async (
    context: SessionSyncContext,
    options?: EnsureSessionOptions,
  ): Promise<EnsureSessionResult> => {
    if (!context.hasAccessTokenLoader) {
      return clearOrPreserveSessionAfterFailure(context, 'auth0-error');
    }

    try {
      logSessionSyncStart(context);
      await auth0Gateway.waitForAuth0ToSettle(context.auth0Client, options?.timeoutMs);

      const postSettleResult = getPostSettleSessionSyncResult(context, options);

      if (postSettleResult) {
        return postSettleResult;
      }

      return await finalizeSessionSync(context, options);
    } catch (error) {
      return handleSessionSyncFailure(context, options, error);
    }
  };

  const trackSessionSyncPromise = (
    sessionSyncKey: string,
    sessionSyncPromise: Promise<EnsureSessionResult>,
  ): Promise<EnsureSessionResult> => {
    const trackedPromise = sessionSyncPromise.finally(() => {
      if (sessionSyncPromises.get(sessionSyncKey) === trackedPromise) {
        sessionSyncPromises.delete(sessionSyncKey);
      }
    });

    sessionSyncPromises.set(sessionSyncKey, trackedPromise);

    return trackedPromise;
  };

  const ensureSession = async (
    options?: EnsureSessionOptions,
    runOptions?: SessionRunOptions,
  ): Promise<EnsureSessionResult> => {
    const cachedSessionResult = getCachedEnsureSessionResult(options, runOptions);

    if (cachedSessionResult) {
      return cachedSessionResult;
    }

    const context = createSessionSyncContext(options, runOptions);
    const canRecoverExpiredLocalSession =
      context.hasAccessTokenLoader &&
      (context.reason === 'app-start' || context.reason === 'manual-refresh');

    if (context.hasLocalSession && isTokenExpired() && !canRecoverExpiredLocalSession) {
      clearAuth();
      return {
        status: 'unauthenticated',
        reason: context.reason,
      };
    }

    const sessionSyncKey = createSessionSyncPromiseKey(options, context, runOptions);
    const inFlightSessionSync = sessionSyncPromises.get(sessionSyncKey);

    if (inFlightSessionSync) {
      return withTimeout(inFlightSessionSync, options?.timeoutMs);
    }

    return trackSessionSyncPromise(sessionSyncKey, runSessionSync(context, options));
  };

  const getRecentTrustedRefreshToken = (
    refreshKey: string,
    targetOrganizationId: string | null,
  ): string | null => {
    const recentRefresh = recentTrustedRefreshes.get(refreshKey);

    if (!recentRefresh) {
      return null;
    }

    if (Date.now() - recentRefresh.completedAt > TRUSTED_AUTH_REFRESH_DEDUPE_WINDOW_MS) {
      recentTrustedRefreshes.delete(refreshKey);
      return null;
    }

    if (isTokenExpired()) {
      return null;
    }

    if (
      getOrganizationId() !== targetOrganizationId ||
      getJwtToken() !== recentRefresh.accessToken ||
      getIdToken() !== recentRefresh.idToken
    ) {
      return null;
    }

    return recentRefresh.accessToken;
  };

  const initializeSession = async (
    options?: Omit<EnsureSessionOptions, 'refresh' | 'freshAuth0Token' | 'reason'>,
  ): Promise<EnsureSessionResult> => {
    return ensureSession({
      ...options,
      reason: 'app-start',
    });
  };

  const hasAuthCallbackRetryAttemptRemaining = (attempt: number): boolean => {
    return attempt < AUTH_CALLBACK_MAX_ATTEMPTS;
  };

  const shouldRetryMissingIdTokenCallbackResult = (
    result: EnsureSessionResult,
    attempt: number,
  ): result is FailedSessionResult => {
    if (result.status !== 'failed') {
      return false;
    }

    if (result.failureReason !== 'missing-id-token') {
      return false;
    }

    if (getJwtToken() !== null || getIdToken() !== null) {
      return false;
    }

    return hasAuthCallbackRetryAttemptRemaining(attempt);
  };

  const shouldRetryAuthCallbackTimeout = (error: unknown, attempt: number): boolean => {
    return isSessionSyncTimeoutError(error) && hasAuthCallbackRetryAttemptRemaining(attempt);
  };

  const completeLoginCallback = async (
    options?: Omit<EnsureSessionOptions, 'reason'>,
  ): Promise<EnsureSessionResult> => {
    const callbackRunSuffix = `${Date.now()}-${Math.random()}`;
    const flowTrace = createAuthSessionFlowTrace({
      reason: 'auth-callback',
      targetOrganizationId: options?.targetOrganizationId ?? null,
      fallbackMode: options?.fallbackMode ?? 'clear-auth',
    });

    for (let attempt = 1; attempt <= AUTH_CALLBACK_MAX_ATTEMPTS; attempt += 1) {
      try {
        const result = await ensureSession(
          {
            ...options,
            refresh: true,
            reason: 'auth-callback',
          },
          {
            dedupeSuffix: `callback:${callbackRunSuffix}:${attempt}`,
            flowTrace: {
              ...flowTrace,
              attempt,
            },
          },
        );

        if (!shouldRetryMissingIdTokenCallbackResult(result, attempt)) {
          return result;
        }

        logAuthSessionFlow(
          'warn',
          'Retrying auth callback session sync',
          { ...flowTrace, attempt },
          {
            failureReason: result.failureReason,
            maxAttempts: AUTH_CALLBACK_MAX_ATTEMPTS,
          },
        );
      } catch (error) {
        if (!shouldRetryAuthCallbackTimeout(error, attempt)) {
          throw error;
        }

        logAuthSessionFlow(
          'warn',
          'Retrying auth callback session sync',
          { ...flowTrace, attempt },
          {
            failureReason: 'timeout',
            maxAttempts: AUTH_CALLBACK_MAX_ATTEMPTS,
          },
        );
      }

      await sleep(AUTH_CALLBACK_RETRY_DELAY_MS * attempt);
    }

    return {
      status: 'failed',
      reason: 'auth-callback',
      failureReason: 'auth0-error',
    };
  };

  const refreshAuthClaims = async (organizationId?: string | null): Promise<string | null> => {
    const targetOrganizationId =
      organizationId ?? getRequiredOrganizationId() ?? getOrganizationId();
    const refreshKey = targetOrganizationId ?? 'organization:none';
    const inFlightRefreshKey = `${refreshKey}::${getJwtToken() ?? 'token:none'}::${
      getIdToken() ?? 'id-token:none'
    }`;
    const recentRefreshToken = getRecentTrustedRefreshToken(refreshKey, targetOrganizationId);

    if (recentRefreshToken) {
      return recentRefreshToken;
    }

    const inFlightRefresh = trustedRefreshPromises.get(inFlightRefreshKey);

    if (inFlightRefresh) {
      return inFlightRefresh;
    }

    const refreshPromise = ensureSession(
      {
        refresh: true,
        freshAuth0Token: true,
        reason: 'manual-refresh',
        targetOrganizationId,
        fallbackMode: 'preserve-existing-session',
      },
      { dedupeSuffix: `trusted-refresh:${inFlightRefreshKey}` },
    )
      .then(async (result) => {
        if (result.status === 'failed' && isMfaRequiredAuth0Error(result.error)) {
          await onMfaRequired?.();

          return null;
        }

        const accessToken = result.status === 'authenticated' ? result.accessToken : null;

        if (accessToken) {
          recentTrustedRefreshes.set(refreshKey, {
            accessToken,
            completedAt: Date.now(),
            idToken: getIdToken(),
            organizationId: targetOrganizationId,
          });
        }

        return accessToken;
      })
      .finally(() => {
        if (trustedRefreshPromises.get(inFlightRefreshKey) === refreshPromise) {
          trustedRefreshPromises.delete(inFlightRefreshKey);
        }
      });

    trustedRefreshPromises.set(inFlightRefreshKey, refreshPromise);

    return refreshPromise;
  };

  const refreshCachedAuthSession = async (
    organizationId?: string | null,
  ): Promise<string | null> => {
    const targetOrganizationId =
      organizationId ?? getRequiredOrganizationId() ?? getOrganizationId();
    const result = await ensureSession({
      refresh: true,
      reason: 'manual-refresh',
      targetOrganizationId,
      fallbackMode: 'preserve-existing-session',
      suppressMfaRedirect: true,
    });

    return result.status === 'authenticated' ? result.accessToken : null;
  };

  const waitForAuthSync = async (): Promise<string | null> => {
    const activeSessionSyncs = [...sessionSyncPromises.values()];

    if (activeSessionSyncs.length > 0) {
      await Promise.allSettled(activeSessionSyncs);
    }

    return getSessionResultFromState()?.accessToken ?? null;
  };

  const getValidToken = async (): Promise<string | null> => {
    const jwtToken = getJwtToken();

    if (jwtToken && getIdToken() && !isTokenExpired()) {
      return jwtToken;
    }

    return null;
  };

  return {
    ensureSession,
    initializeSession,
    completeLoginCallback,
    refreshAuthClaims,
    refreshCachedAuthSession,
    waitForAuthSync,
    getValidToken,
  };
};
